import { Injectable } from '@angular/core';
import * as moment from 'moment';
import * as loadImage from 'blueimp-load-image';
import { MlImageDeduplicationService } from './ml-image-deduplication.service';
import { PhotoGridService } from 'app/services/photo-grid.service';
import { ImageModel } from './_models/image.model';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Injectable()
export class ImagePreprocessingService {

  public $tempImages: ImageModel[] = [];
  public $images: ImageModel[] = [];
  private _images: BehaviorSubject<ImageModel[]> = new BehaviorSubject<ImageModel[]>([]);
  public images: Observable<ImageModel[]> = this._images.asObservable();

  $imagePreprocessCount = 0;
  private _imagePreprocessCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public imagePreprocessCount: Observable<number> = this._imagePreprocessCount.asObservable();

  $dedupliCationCount = 0;
  private _dedupliCationCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public dedupliCationCount: Observable<number> = this._dedupliCationCount.asObservable();

  private _totalPhotoCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public totalPhotoCount: Observable<number> = this._totalPhotoCount.asObservable();

  public _isPreprocessing: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  public isPreprocessing: Observable<any> = this._isPreprocessing.asObservable();

  public duplicationSubscription: Subscription;


  constructor(
    public photoGridService: PhotoGridService,
    private mlImageDeduplicationService: MlImageDeduplicationService
  ) {
  }

  public async preprocess(items) {
    this._isPreprocessing.next(true);
    this.$imagePreprocessCount = 0;
    this._imagePreprocessCount.next(this.$imagePreprocessCount);
    this._totalPhotoCount.next(items.length);

    for (let index = 0; index < items.length; index++) {
      const element = items[index];
      const image = new ImageModel();
      image.duplicateList = [];
      image.unchecked = false;

      await loadImage(element._file, { meta: true, canvas: true, maxWidth: 400, maxHeight: 400, orientation: true })
        .then((data) => {
          image.rawPreview = data.image,
            image.preview = data.image.toDataURL('image/jpeg');
          image.previewWidth = data.image.width;
          image.previewHeight = data.image.height;
        }).catch((err) => {
          console.log(err, element);
        });
      // Image originals and details
      await loadImage(element._file, { meta: true, maxWidth: 400, maxHeight: 400, orientation: 1 })
        .then((data) => {
          const exif = data.exif ? data.exif.getAll()['Exif'] : null;
          const GPSInfo = data.exif ? data.exif.getAll()['GPSInfo'] : null;
          const o = {
            0: 0,
            1: 0,
            2: 0,
            3: 180,
            4: 180,
            5: 270,
            6: 270,
            7: 90,
            8: 90,
          };

          image.name = element._file.name;
          image.originalFile = element._file;
          image.originalWidth = data.originalWidth;
          image.originalHeight = data.originalHeight;
          image.date = exif ? this.extractDate(element._file, exif) : new Date();
          image.orientation = data.exif ? o[data.exif.get('Orientation')] || 0 : 0;
          image.latitude = GPSInfo ? this.extractLatitude(GPSInfo) : 0;
          image.longitude = GPSInfo ? this.extractLongitude(GPSInfo) : 0;
          image.dataUrl = data.image.toDataURL('image/jpeg');

          this.$images.push(image);
          this.$images.sort((a, b) => {
            return a.date.valueOf() - b.date.valueOf();
          });
          this._images.next(this.$images);

          this.$imagePreprocessCount++;
          this._imagePreprocessCount.next(this.$imagePreprocessCount);
        });
    }
    this._isPreprocessing.next(false);

    this.duplicationSubscription = this.photoGridService.dedup.pipe(takeWhile(r => r !== true, true)).subscribe(async r => {
      if (r) {
        this.photoGridService.isDeduping(true);
        await this.similarityCheck(this.$images);
        this.buildGridWithDuplicates(this.$images);
        this.photoGridService.isDeduping(false);
      }
      this._images.next(this.$images);
    });
  }



  extractDate(file, exif) {
    let date = exif && (exif['DateTimeOriginal'] || exif['DateTimeDigitized']);
    if (!date) {
      if (file.lastModified) {
        date = new Date(file.lastModified);
      } else if (file.lastModifiedDate) {
        date = file.lastModifiedDate;
      }
    } else {
      date = moment(date, 'YYYY-MM-DD HH:mm:ss').toDate();
    }
    return date;
  }

  extractLatitude(GPSInfo) {
    let latitude;
    const GPSLatitudeRef = GPSInfo['GPSLatitudeRef'];
    const GPSLatitude = GPSInfo['GPSLatitude'];
    if (GPSLatitudeRef && GPSLatitude) {
      latitude = this.normalizeLatLng(GPSLatitude, GPSLatitudeRef);
    }
    return latitude;
  }

  extractLongitude(GPSInfo) {
    let longitude;
    const GPSLongitudeRef = GPSInfo['GPSLongitudeRef'];
    const GPSLongitude = GPSInfo['GPSLongitude'];
    if (GPSLongitudeRef && GPSLongitude) {
      longitude = this.normalizeLatLng(GPSLongitude, GPSLongitudeRef);
    }
    return longitude;
  }

  normalizeLatLng(dms, ref) {
    dms = dms.split(',');
    const degrees = Number(dms[0]);
    const minutes = Number(dms[1]);
    const seconds = Number(dms[2]);
    const decimal = ((minutes * 60) + seconds) / 3600;
    let decimalDegrees = decimal + degrees;

    if (ref === 'S' || ref === 'W') {
      decimalDegrees = -decimalDegrees;
    }

    return decimalDegrees.toFixed(5);
  }

  async similarityCheck(imageArray: ImageModel[]) {
    let similarityCounter = 0;
    let isDedupenabled;
    this.photoGridService.dedup.subscribe(result => {
      isDedupenabled = result;
    });
    for (let index = 0; index < imageArray.length; index++) {
      if (isDedupenabled) {
        const previousItem = imageArray[index - 1];
        if (previousItem && imageArray[index].similarity === undefined) {
          similarityCounter++;
          this._dedupliCationCount.next(similarityCounter);
          const timeDelta = (imageArray[index].date.getTime() / 1000 - previousItem.date.getTime() / 1000);
          if (timeDelta < 120) {
            imageArray[index].similarity = await this.mlImageDeduplicationService.start(
              imageArray[index].rawPreview, previousItem.rawPreview, timeDelta
            );
          } else {
            imageArray[index].similarity = 0;
          }
        }
      }
    }
  }

  buildGridWithDuplicates(images: ImageModel[]) {
    let isDedupenabled;
    this.photoGridService.dedup.subscribe(result => {
      isDedupenabled = result;
    });
    if (isDedupenabled) {
      this.next(images, images[0], 0, images[1], 1);
    }
  }

  next(imageList: ImageModel[], origin: ImageModel, originIndex, candidate: ImageModel, candidateIndex: number) {
    if (imageList.length === candidateIndex) {
      return;
    }
    if (candidate.similarity > 0.7) {
      if (candidate.postprocessed !== true) {
        candidate.isDuplicate = true;
        candidate.unchecked = true;
        if (origin.duplicateList.length === 0) {
          origin.duplicateList.push(origin);
        }
        if (!origin.duplicateList.find(c => c === candidate)) {
          origin.duplicateList.push(candidate);
        }
      }
      this.next(imageList, origin, originIndex, imageList[candidateIndex + 1], candidateIndex + 1);
      candidate.postprocessed = true;
    } else {
      if (candidate.postprocessed !== true) {
        candidate.isDuplicate = false;
        candidate.unchecked = false;
      }
      this.next(imageList, candidate, candidateIndex, imageList[candidateIndex + 1], candidateIndex + 1);
      candidate.postprocessed = true;
    }
}


clear() {
  this.$images = [];
  this._images.next([]);
  this._dedupliCationCount.next(0);
  this.$dedupliCationCount = 0;
  if (this.duplicationSubscription) {
    this.duplicationSubscription.unsubscribe();
  }
  this.photoGridService.dedupEnabled(false);
}
}
